import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { VJFPUserService } from '../../../services/VJFPUserService'
import { useAuth } from 'app/context/auth-context'
import { Card } from 'react-bootstrap-v5'
import { CardBody, CardHeader } from 'react-bootstrap'
import { AppTopNav } from '../AppTopNav'
import { BasePageProps, Roles } from 'app/types'
import useIsMounted from 'app/hooks/useIsMounted'
import { AlertModal } from '../modals/AlertModal'
import { UrlParamReplace } from 'app/helpers/MiscUtils'
import { JSRoutes } from 'app/modules/job-seeker/JSPage'
import { useHistory } from 'react-router-dom'
import { EmployerAdminRoutes } from 'app/modules/employer-admin/EmployerAdminPage'
import { RecruiterRoutes } from 'app/modules/recruiter/RecruiterPage'
import { Storage, Amplify } from 'aws-amplify'
Amplify.configure({
    Storage: {
        AWSS3: {
            region: 'us-east-1',
            bucket: 'videojobfairs-docs'
        }
    }
});
const baseSchema = {
    firstName: Yup.string()
        .min(3, 'Minimum 3 characters')
        .max(50, 'Maximum 50 characters')
        .required('First name is required'),
    lastName: Yup.string()
        .min(3, 'Minimum 3 characters')
        .max(50, 'Maximum 50 characters')
        .required('Last name is required'),
    email: Yup.string()
        .email('Invalid email format')
}

const applicantSchema = {
    ...baseSchema,
    phone_number: Yup.string()
        .required('Phone number is required')
        .matches(/^\+1\d{10}$/, 'Phone number must be in format +19999999999'),
    linkedIn_url: Yup.string()
        .url('Must be a valid URL'),
    years_of_experience: Yup.number()
        .required('Years of experience is required')
        .min(0, 'Must be at least 0')
        .max(50, 'Must be less than 50'),
    salary_requirements: Yup.string()
        .required('Salary requirements is required'),
    geographic_preference: Yup.string()
        .required('Geographic preference is required'),

    security_clearance: Yup.string()
        .required('Security clearance is required'),
    willing_to_relocate: Yup.boolean()
        .required('Relocation preference is required'),
    degree_type: Yup.string()
        .required('Degree type is required'),
    academic_major: Yup.string()
        .required('Academic major is required'),
    field_of_work: Yup.string()
        .required('Field of work is required'),
    availability_date: Yup.string()
        .required('Availability date is required')
}

const profileSchema = (roles: string[] = []) => {
    return Yup.object().shape(
        roles.includes('Applicant') ? applicantSchema : baseSchema
    )
}

const degreeTypes = ['High School', 'Associates', 'Bachelors', 'Masters', 'Doctorate']
const academicMajors = ['Computer Science', 'Engineering', 'Business', 'Mathematics']
const fieldsOfWork = ['Software Development', 'Data Science', 'Project Management']
const salaryRanges = ['$50,000-$75,000', '$75,000-$100,000', '$100,000-$150,000', '$150,000+']
const securityClearanceOptions = ['None', 'Secret', 'Top Secret', 'TS/SCI']

const geographicRegions = [
    'Northeast',
    'Northwest',
    'Southeast',
    'Southwest',
    'Central',
    'Mountain',
    'Pacific',
    'All'
]

export const ManageProfile = (props: BasePageProps) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false)
    const vjfpUserService = new VJFPUserService()
    const history = useHistory();
    const [showSavedSuccessfullyModal, setShowSavedSuccessfullyModal] = useState<boolean>(false)
    const isMounted = useIsMounted("ManageProfile");
    const [isResumeUploading, setIsResumeUploading] = useState(false);
    const [resumeFileName, setResumeFileName] = useState<string | null>(null);
    const [resumeUrl, setResumeUrl] = useState<string>('');

    const initialValues = {
        firstName: '',
        lastName: '',
        email: '',
        phone_number: '',
        linkedIn_url: 'https://linkedin.com/',
        degree_type: '',
        academic_major: '',
        field_of_work: '',
        years_of_experience: 0,
        salary_requirements: '',
        resume_url: '',
        availability_date: '',
        geographic_preference: '',
        security_clearance: '',
        willing_to_relocate: false
    }

    const handleResumeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.currentTarget.files;
        if (files && files.length > 0) {
            const file = files[0];
            setIsResumeUploading(true);
            try {
                const fileExtension = file.name.split('.').pop();
                const resumePath = await Storage.put(
                    `attached_resumes/${user?.id}_resume.${fileExtension}`,
                    file,
                    {
                        contentType: file.type
                    }
                );
                formik.setFieldValue('resume_url', resumePath.key);
                setResumeFileName(file.name);
            } catch (err) {
                console.error('Error uploading resume:', err);
            } finally {
                setIsResumeUploading(false);
            }
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema: profileSchema(user?.roles),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            if (!user?.organizationId) return;
            setLoading(true)
            try {
                await vjfpUserService.updateProfile(user.organizationId, user.id, {
                    firstName: values.firstName || '',
                    lastName: values.lastName || '',
                    emailId: values.email || '',
                    phone_number: values.phone_number || '',
                    linkedIn_url: values.linkedIn_url || 'https://linkedin.com/',
                    degree_type: values.degree_type || '',
                    academic_major: values.academic_major || '',
                    field_of_work: values.field_of_work || '',
                    years_of_experience: values.years_of_experience || 0,
                    salary_requirements: values.salary_requirements || '',
                    resume_url: values.resume_url || '',
                    availability_date: values.availability_date || '',
                    geographic_preference: values.geographic_preference || '',
                    security_clearance: values.security_clearance || '',
                    willing_to_relocate: values.willing_to_relocate || false,
                    isProfileComplete: true
                });

                setShowSavedSuccessfullyModal(true);

            } catch (error) {
                console.error('Error updating profile:', error)
            } finally {
                setLoading(false)
            }
        }
    })

    useEffect(() => {
        const loadUserProfile = async () => {
            if (!user?.id || !user?.organizationId) return;
            const userProfile = await vjfpUserService.getById(
                user.organizationId,
                user.id
            )
            if (userProfile && isMounted()) {
                formik.setValues({
                    firstName: userProfile.firstName || '',
                    lastName: userProfile.lastName || '',
                    email: userProfile.emailId || '',
                    phone_number: userProfile.phone_number || '',
                    linkedIn_url: userProfile.linkedIn_url || 'https://linkedin.com/',
                    degree_type: userProfile.degree_type || '',
                    academic_major: userProfile.academic_major || '',
                    field_of_work: userProfile.field_of_work || '',
                    years_of_experience: userProfile.years_of_experience || 0,
                    salary_requirements: userProfile.salary_requirements || '',
                    resume_url: userProfile.resume_url || '',
                    availability_date: userProfile.availability_date || '',
                    geographic_preference: userProfile.geographic_preference || '',
                    security_clearance: userProfile.security_clearance || '',
                    willing_to_relocate: userProfile.willing_to_relocate || false
                })
            }
        }
        loadUserProfile()
    }, [user])

    useEffect(() => {
        const getResumeUrl = async () => {
            if (formik.values.resume_url) {
                const url = await Storage.get(formik.values.resume_url);
                setResumeUrl(url);
            }
        };
        
        getResumeUrl();
    }, [formik.values.resume_url]);

    return (
        <div className="chatPage">
            <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs}>
            </AppTopNav>
            <Card className='mb-5 mb-xl-10'>
                <CardHeader className='border-0 cursor-pointer'>
                    <div className='card-title m-0'>
                        <h3 className='fw-bolder m-0'>Profile Details</h3>
                    </div>
                </CardHeader>
                <CardBody className='py-4'>
                    <div className='container-fluid'>
                        <form className='form mw-1000px' onSubmit={formik.handleSubmit} noValidate>
                            <div className='card-body p-9'>
                                {/* Basic Info Section */}
                                <div className='row align-items-center mb-6'>
                                    <label className='col-lg-3 col-form-label required fw-bold fs-6'>Email</label>
                                    {/* Email Input */}
                                    <div className='col-lg-6'>
                                        <input
                                            type='email'
                                            className={`form-control form-control-lg form-control-solid ${formik.errors.email && formik.touched.email ? 'is-invalid' : ''
                                                }`}
                                            placeholder='Email'
                                            {...formik.getFieldProps('email')}
                                            readOnly
                                        />
                                        {formik.errors.email && formik.touched.email && (
                                            <div className='invalid-feedback d-block'>{formik.errors.email}</div>
                                        )}
                                    </div>
                                </div>

                                <div className='row align-items-center mb-6'>
                                    <label className='col-lg-3 col-form-label required fw-bold fs-6'>Full Name</label>
                                    <div className='col-lg-6'>
                                        <div className='row'>
                                            {/* First Name Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='text'
                                                    className={`form-control form-control-lg form-control-solid mb-3 mb-lg-0 ${formik.errors.firstName && formik.touched.firstName ? 'is-invalid' : ''
                                                        }`}
                                                    placeholder='First name'
                                                    {...formik.getFieldProps('firstName')}
                                                />
                                                {formik.errors.firstName && formik.touched.firstName && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.firstName}</div>
                                                )}
                                            </div>
                                            {/* Last Name Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='text'
                                                    className={`form-control form-control-lg form-control-solid ${formik.errors.lastName && formik.touched.lastName ? 'is-invalid' : ''
                                                        }`}
                                                    placeholder='Last name'
                                                    {...formik.getFieldProps('lastName')}
                                                />
                                                {formik.errors.lastName && formik.touched.lastName && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.lastName}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Applicant-specific fields */}
                                {user?.roles?.includes('Applicant') && (
                                    <>
                                         {/* Resume */}
                                      <div className='separator separator-dashed my-6'></div>
                                        <h3 className='text-dark fw-bolder mb-6'>Resume</h3>
                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Upload Resume</label>
                                            <div className='col-lg-6'>
                                                <div className="d-flex align-items-center">
                                                    <label htmlFor="resume" className="btn btn-primary me-3">
                                                        <i className="fas fa-upload me-2"></i>
                                                        {isResumeUploading ? 'Uploading...' : 'Select Resume'}
                                                        <input
                                                            id="resume"
                                                            name="resume"
                                                            type="file"
                                                            accept=".pdf,.doc,.docx,.txt"
                                                            className="d-none"
                                                            onChange={handleResumeChange}
                                                            disabled={isResumeUploading}
                                                        />
                                                    </label>
                                                    {resumeFileName && (
                                                        <span className="text-muted">{resumeFileName}</span>
                                                    )}
                                                </div>
                                                {formik.touched.resume_url && formik.errors.resume_url && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.resume_url}</div>
                                                )}
                                                {resumeUrl && (
                                                    <div className="mt-2">
                                                        <a 
                                                            href={resumeUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-primary"
                                                        >
                                                            View Current Resume
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    {/* Contact Info */}
                                        <div className='separator separator-dashed my-6'></div>
                                        <h3 className='text-dark fw-bolder mb-6'>Contact Information</h3>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label fw-bold fs-6'>Phone Number</label>
                                            {/* Phone Number Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='text'
                                                    className={`form-control form-control-lg form-control-solid ${formik.errors.phone_number && formik.touched.phone_number ? 'is-invalid' : ''
                                                        }`}
                                                    placeholder='Phone number'
                                                    {...formik.getFieldProps('phone_number')}
                                                />
                                                {formik.errors.phone_number && formik.touched.phone_number && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.phone_number}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label fw-bold fs-6'>LinkedIn URL</label>
                                            {/* LinkedIn URL Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='url'
                                                    className={`form-control form-control-lg form-control-solid ${formik.errors.linkedIn_url && formik.touched.linkedIn_url ? 'is-invalid' : ''
                                                        }`}
                                                    placeholder='LinkedIn URL'
                                                    {...formik.getFieldProps('linkedIn_url')}
                                                />
                                                {formik.errors.linkedIn_url && formik.touched.linkedIn_url && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.linkedIn_url}</div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Education and Experience */}
                                        <div className='separator separator-dashed my-6'></div>
                                        <h3 className='text-dark fw-bolder mb-6'>Education & Experience</h3>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Degree Type</label>
                                            {/* Degree Type Select */}
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.degree_type && formik.touched.degree_type ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('degree_type')}
                                                >
                                                    <option value=''>Select Degree</option>
                                                    {degreeTypes.map((type) => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.degree_type && formik.touched.degree_type && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.degree_type}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Academic Major</label>
                                            {/* Academic Major Select */}
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.academic_major && formik.touched.academic_major ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('academic_major')}
                                                >
                                                    <option value=''>Select Major</option>
                                                    {academicMajors.map((major) => (
                                                        <option key={major} value={major}>{major}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.academic_major && formik.touched.academic_major && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.academic_major}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Work Preferences */}
                                        <div className='separator separator-dashed my-6'></div>
                                        <h3 className='text-dark fw-bolder mb-6'>Work Preference</h3>
                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Field of Work</label>
                                            {/* Field of Work Select */}
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.field_of_work && formik.touched.field_of_work ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('field_of_work')}
                                                >
                                                    <option value=''>Select Field</option>
                                                    {fieldsOfWork.map((field) => (
                                                        <option key={field} value={field}>{field}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.field_of_work && formik.touched.field_of_work && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.field_of_work}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Years of Experience</label>
                                            {/* Years of Experience Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='number'
                                                    className={`form-control form-control-lg form-control-solid ${formik.errors.years_of_experience && formik.touched.years_of_experience ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('years_of_experience')}
                                                />
                                                {formik.errors.years_of_experience && formik.touched.years_of_experience && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.years_of_experience}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Salary Requirements</label>
                                            {/* Salary Requirements Select */}
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.salary_requirements && formik.touched.salary_requirements ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('salary_requirements')}
                                                >
                                                    <option value=''>Select Range</option>
                                                    {salaryRanges.map((range) => (
                                                        <option key={range} value={range}>{range}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.salary_requirements && formik.touched.salary_requirements && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.salary_requirements}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Security Clearance</label>
                                            {/* Security Clearance Select */}
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.security_clearance && formik.touched.security_clearance ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('security_clearance')}
                                                >
                                                    <option value=''>Select Clearance</option>
                                                    {securityClearanceOptions.map((clearance) => (
                                                        <option key={clearance} value={clearance}>{clearance}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.security_clearance && formik.touched.security_clearance && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.security_clearance}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Geographic Preference</label>
                                            <div className='col-lg-6 fv-row'>
                                                <select
                                                    className={`form-select form-select-solid form-select-lg ${formik.errors.geographic_preference && formik.touched.geographic_preference ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('geographic_preference')}
                                                >
                                                    <option value=''>Select Region</option>
                                                    {geographicRegions.map((region) => (
                                                        <option key={region} value={region}>{region}</option>
                                                    ))}
                                                </select>
                                                {formik.errors.geographic_preference && formik.touched.geographic_preference && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.geographic_preference}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Availability Date</label>
                                            {/* Availability Date Input */}
                                            <div className='col-lg-6 fv-row'>
                                                <input
                                                    type='date'
                                                    className={`form-control form-control-lg form-control-solid ${formik.errors.availability_date && formik.touched.availability_date ? 'is-invalid' : ''
                                                        }`}
                                                    {...formik.getFieldProps('availability_date')}
                                                />
                                                {formik.errors.availability_date && formik.touched.availability_date && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.availability_date}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='row align-items-center mb-6'>
                                            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Willing to Relocate</label>
                                            <div className='col-lg-6 fv-row'>
                                                <div className='form-check form-switch form-check-custom form-check-solid'>
                                                    <input
                                                        className='form-check-input'
                                                        type='checkbox'
                                                        {...formik.getFieldProps('willing_to_relocate')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className='separator separator-dashed my-6'></div>


                                <div className='d-flex justify-content-center'>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                        disabled={loading || !(formik.isValid && formik.dirty)}
                                    >
                                        {!loading && 'Save Changes'}
                                        {loading && (
                                            <span className='indicator-progress' style={{ display: 'block' }}>
                                                Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                            
                        </form>
                    </div>
                </CardBody>
            </Card>
            <AlertModal show={showSavedSuccessfullyModal} onOK={() => {
                setShowSavedSuccessfullyModal(false);
                if (user?.roles?.includes(Roles.Applicant))
                    history.push(UrlParamReplace(JSRoutes.Lobby));
                else if (user?.roles?.includes(Roles.EmployerAdmin))
                    history.push(EmployerAdminRoutes.Dashboard);
                else if (user?.roles?.includes(Roles.Recruiter)) {
                    history.push(RecruiterRoutes.Booth);
                }
            }}>
                Profile Information saved successfully !
            </AlertModal>
        </div>
    )
}
